import { toast } from "sonner";
import * as React from "react";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { Order } from "../../helpers/TableHelpers";
import { FlagType } from "../../models/ClientConfiguration/FlaggingConfiguration";
import { JobRequestStatus } from "../../models/Jobs/JobRequestStatus";
import { DeleteFirmDocumentsFolderRequest } from "../../models/Jobs/JobRequest";
import { JobType } from "../../models/Jobs/JobType";
import { JobEntityType } from "../../models/Jobs/JobEntityType";
import { FirmDocumentsFolder } from "../../models/FirmDocumentFolder/FirmDocumentsFolder";
import { FilterCombobox } from "../common/FilterCombobox";
import MigrationStatusChip from "../common/MigrationStatusChip";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import TableEmptyState from "../common/TableEmptyState";
import FlagTypeChip from "../common/FlagTypeChip";
import { ActiveFirmDocumentsFolderJobsExplorer } from "../../pages/FirmDocumentsFoldersPage";
import FirmDocumentsFoldersRepository from "../../repositories/FirmDocumentsFoldersRepository";
import {MigrationStatus, MigrationStatusColors, MigrationStatusIcons, MigrationStatusMigrationEntityLabels} from "../../models/MigrationStatus";
import {UserRole} from "../../models/User";
import ConfirmMigrationEntityBlockUpdateModal, {MIGRATION_ENTITY_BLOCK_UPDATE_CONFIGS} from "../common/ConfirmMigrationEntityBlockUpdateModal";
import TextHighlighter from "../common/TextHighlighter";
import {Trash2, TableProperties, Lock, LockOpen, Ban, AlertTriangle, CheckCircle2, FileWarning} from "lucide-react";
import {cn} from "@/lib/utils";
import { colorVariantClasses } from "@/theme/AppColorVariant";
import {Table, TableHeader, TableBody, TableRow, TableHead, TableCell} from "@/components/ui/table";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip";
import {Button} from "@/components/ui/button";
import {Label} from "@/components/ui/label";
import {Badge} from "@/components/ui/badge";
import {
  fileFiltersBoxClassName,
  filterComboboxClassName,
  tableWrapperClassName,
} from "../common/Table/TableStyles";
import { SearchInput } from "../common/Table/SearchInput";
import { FlagStatusFilter, useFlagStatusFilter } from "../common/Table/FlagStatusFilter";
import TablePagination from "../common/Table/TablePagination";
import { useUserLookup } from "../../hooks/useUserLookup";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { deletionWouldStrandUploads, STRANDED_UPLOADS_HINT } from "../../lib/migrationEntityDeletion";

interface FirmDocumentsFoldersTableProps {
  organizationId: string;
  activeJobsExplorer: ActiveFirmDocumentsFolderJobsExplorer;
  newFirmDocumentsFoldersForDeletion: DeleteFirmDocumentsFolderRequest[];
  setNewFirmDocumentsFoldersForDeletion: React.Dispatch<React.SetStateAction<DeleteFirmDocumentsFolderRequest[]>>;
  newFirmDocumentsFoldersForExport: FirmDocumentsFolder[];
  setNewFirmDocumentsFoldersForExport: React.Dispatch<React.SetStateAction<FirmDocumentsFolder[]>>;
  userRole: UserRole;
  isAssignedUser: boolean;
}

const FirmDocumentsFoldersTable: React.FC<FirmDocumentsFoldersTableProps> = ({
  organizationId,
  activeJobsExplorer,
  newFirmDocumentsFoldersForDeletion,
  setNewFirmDocumentsFoldersForDeletion,
  newFirmDocumentsFoldersForExport,
  setNewFirmDocumentsFoldersForExport,
  userRole,
  isAssignedUser
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getUserName } = useUserLookup();
  const firmDocumentsFoldersRepository = new FirmDocumentsFoldersRepository();
  const [order, setOrder] = React.useState<Order>("asc");
  const [sort, setSort] = React.useState<string>("status");
  const [lockFilter, setLockFilter] = React.useState<boolean | null>(null);
  const [statusFilter, setStatusFilter] = React.useState<string | null>(null);
  const [flaggedPathsFilter, setFlaggedPathsFilter] = React.useState<boolean | null>(null);
  const [conflictsFilter, setConflictsFilter] = React.useState<boolean | null>(null);
  const [searchForFolderPath, setSearchForFolderPath] = useState<string | null>("");
  const [page, setPage] = React.useState(0);
  const [flagStatusFilter, setFlagStatusFilter] = useFlagStatusFilter(() => setPage(0));
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const [firmDocumentsFolders, setFirmDocumentsFolders] = useState<FirmDocumentsFolder[]>([]);
  const [rows, setRows] = useState(0);
  const [dirtyFirmDocumentsFolderId, setDirtyFirmDocumentsFolderId] = useState<string>("");
  const [dirtyFolderPath, setDirtyFolderPath] = useState<string>("");
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = React.useState<boolean>(false);

  const firmDocumentsFoldersQuery = useQuery({ queryKey: [
      QUERY_KEYS.FIRM_DOCUMENTS_FOLDERS,
      organizationId,
      searchForFolderPath,
      page,
      rowsPerPage,
      statusFilter,
      lockFilter,
      flaggedPathsFilter,
      conflictsFilter,
      flagStatusFilter,
      sort,
      order,
    ], queryFn:
    async () => {
      return await firmDocumentsFoldersRepository.getFirmDocumentsFolders({
        organizationId: organizationId,
        folderPath: searchForFolderPath ? [searchForFolderPath] : null,
        descending: order === "desc",
        orderBy: sort,
        status: statusFilter ? [statusFilter] : null,
        isLocked: lockFilter,
        hasFlaggedPaths: flaggedPathsFilter,
        hasConflicts: conflictsFilter,
        flagStatus: flagStatusFilter ? [flagStatusFilter] : null,
        pageNumber: page + 1,
        pageSize: rowsPerPage,
      });
    }, staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000 + 1,
      refetchIntervalInBackground: true,
      refetchOnMount: true, });

  useEffect(() => {
    if (firmDocumentsFoldersQuery.data?.data) {
      setFirmDocumentsFolders(firmDocumentsFoldersQuery.data?.data);
      setRows(firmDocumentsFoldersQuery.data.paging?.totalCount ?? 0);
    }
  }, [firmDocumentsFoldersQuery]);

  const handleSearch = (newFolderPath: string) => {
    setSearchForFolderPath(newFolderPath ?? null);
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const changeSort = (newSort: string) => {
    setPage(0);
    setSort(newSort);
  };

  const changeOrder = (newOrder: Order) => {
    setPage(0);
    setOrder(newOrder);
  };

  const handleChangeRowsPerPage = (value: string) => {
    setRowsPerPage(parseInt(value, 10));
    setPage(0);
  };

  const handleDelete = (firmDocumentsFolderId: string, folderPath: string) => {
    if (!disabledRows.has(firmDocumentsFolderId)) {
      setNewFirmDocumentsFoldersForDeletion((prevRows) => [
        ...prevRows,
        {
          migrationEntityId: firmDocumentsFolderId,
          folderPath: folderPath,
          status: JobRequestStatus.New,
          type: JobType.Delete,
          entityType: JobEntityType.FirmDocumentsFolder,
          organizationId: organizationId,
        } as DeleteFirmDocumentsFolderRequest,
      ]);
    }
  };

  const handleAddFirmDocumentsFolderForExport = (firmDocumentsFolderEntity: FirmDocumentsFolder) => {
    if (!disabledRows.has(firmDocumentsFolderEntity.id))
      setNewFirmDocumentsFoldersForExport((prevRows) => [...prevRows, firmDocumentsFolderEntity]);
  };

  const getDisabledRows = () => {
    switch (activeJobsExplorer) {
      case ActiveFirmDocumentsFolderJobsExplorer.DeleteFirmDocumentsFolders:
        return new Set(newFirmDocumentsFoldersForDeletion.map((c) => c.migrationEntityId));
      case ActiveFirmDocumentsFolderJobsExplorer.ExportCsv:
        return new Set(newFirmDocumentsFoldersForExport.map((c) => c.id));
      default:
        return new Set<string>();
    }
  };

  const getActiveButtonForExplorer = (firmDocumentsFolderEntity: FirmDocumentsFolder, isDisabled: boolean) => {
    if (userRole === UserRole.Viewer) {
      return <></>;
    }
    switch (activeJobsExplorer) {
      case ActiveFirmDocumentsFolderJobsExplorer.DeleteFirmDocumentsFolders:
        return (
          <span title={deletionWouldStrandUploads(firmDocumentsFolderEntity) ? STRANDED_UPLOADS_HINT : undefined}>
            <Button variant="ghost" size="icon-sm" className="text-destructive" aria-label="Delete"
              onClick={() => handleDelete(firmDocumentsFolderEntity.id, firmDocumentsFolderEntity.folderPath)}
              disabled={isDisabled || deletionWouldStrandUploads(firmDocumentsFolderEntity)}>
              <Trash2 className="size-4" />
            </Button>
          </span>
        );
      case ActiveFirmDocumentsFolderJobsExplorer.ExportCsv:
        return (
          <Button variant="ghost" size="icon-sm" className="text-emerald-600" aria-label="Csv"
            onClick={() => handleAddFirmDocumentsFolderForExport(firmDocumentsFolderEntity)}
            disabled={isDisabled}>
            <TableProperties className="size-4" />
          </Button>
        );
      default:
        <></>;
    }
  };

  const renderFilters = () => (
    <React.Fragment>
      <div className={filterComboboxClassName}>
        <Label className="text-xs font-semibold text-muted-foreground">Status</Label>
        <FilterCombobox
          value={statusFilter}
          onChange={(newValue) => { setPage(0); setStatusFilter(newValue); }}
          placeholder="Status"
          options={Object.values(MigrationStatus)
            .filter((v) =>
              v !== MigrationStatus.NotFound
              && v !== MigrationStatus.InterruptedDelta
              && v !== MigrationStatus.NotFoundMarkedForRetry
              && v !== MigrationStatus.SkippedDuplicate)
            .map((status) => {
              const migrationStatus = status as MigrationStatus;
              return {
                value: migrationStatus,
                label: MigrationStatusMigrationEntityLabels[migrationStatus],
                color: MigrationStatusColors[migrationStatus],
                icon: MigrationStatusIcons[migrationStatus],
              };
            })}
        />
      </div>
      <div className={filterComboboxClassName}>
        <Label className="text-xs font-semibold text-muted-foreground">Lock</Label>
        <FilterCombobox
          value={lockFilter === null ? null : lockFilter ? "locked" : "unlocked"}
          onChange={(newValue) => { setPage(0); if (newValue === null || newValue === "all") { setLockFilter(null); } else { setLockFilter(newValue === "locked"); } }}
          placeholder="Any"
          options={[
            { value: "locked", label: "Locked", icon: Lock },
            { value: "unlocked", label: "Unlocked", icon: LockOpen },
          ]}
        />
      </div>
      <div className={filterComboboxClassName}>
        <Label className="text-xs font-semibold text-muted-foreground">Flagged paths</Label>
        <FilterCombobox
          value={flaggedPathsFilter === null ? null : flaggedPathsFilter ? "has-flagged-paths" : "no-flagged-paths"}
          onChange={(newValue) => { setPage(0); if (newValue === null) { setFlaggedPathsFilter(null); } else { setFlaggedPathsFilter(newValue === "has-flagged-paths"); } }}
          placeholder="Any"
          options={[
            { value: "has-flagged-paths", label: "Has flagged paths", icon: AlertTriangle },
            { value: "no-flagged-paths", label: "No flagged paths", icon: CheckCircle2 },
          ]}
        />
      </div>
      <div className={filterComboboxClassName}>
        <Label className="text-xs font-semibold text-muted-foreground">Conflicts</Label>
        <FilterCombobox
          value={conflictsFilter === null ? null : conflictsFilter ? "has-conflicts" : "no-conflicts"}
          onChange={(newValue) => { setPage(0); if (newValue === null || newValue === "all") { setConflictsFilter(null); } else { setConflictsFilter(newValue === "has-conflicts"); } }}
          placeholder="Any"
          options={[
            { value: "has-conflicts", label: "Has conflicts", icon: FileWarning },
            { value: "no-conflicts", label: "No conflicts", icon: CheckCircle2 },
          ]}
        />
      </div>
      <FlagStatusFilter value={flagStatusFilter} onChange={(v) => { setPage(0); setFlagStatusFilter(v); }} />
    </React.Fragment>
  );

  const handleBlockModal = (folderPath: string, firmDocumentsFolderId: string, isBlocked: boolean, isOpen: boolean) => {
    setIsBlockModalOpen(isOpen);
    setDirtyFolderPath(folderPath);
    setDirtyFirmDocumentsFolderId(firmDocumentsFolderId);
    setIsBlocked(isBlocked);
  };

  const { isError, isFetching } = firmDocumentsFoldersQuery;
  const disabledRows = getDisabledRows();
  const loading = isFetching || isError;

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <div className={fileFiltersBoxClassName}>
        <SearchInput
          label="Search folders"
          placeholder="Folder path"
          onSearch={(value) => handleSearch(value)}
        />
        {renderFilters()}
      </div>
      <div className={cn(tableWrapperClassName, loading && "opacity-50 pointer-events-none")}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[14%]"><SortableTableColumnHeader label={"Path"} sortProperty={"folderPath"} getSort={() => sort} setSort={(newSort) => changeSort(newSort)} getOrder={() => order} setOrder={(newOrder) => changeOrder(newOrder)} /></TableHead>
              <TableHead className="w-[5%]">Lock</TableHead>
              <TableHead className="w-[11%]"><SortableTableColumnHeader label={"Status"} sortProperty={"status"} getSort={() => sort} setSort={(newSort) => changeSort(newSort)} getOrder={() => order} setOrder={(newOrder) => changeOrder(newOrder)} /></TableHead>
              <TableHead className="w-[7%]">Progress</TableHead>
              <TableHead className="w-[8%]"><SortableTableColumnHeader label={"Files"} sortProperty={"totalNumberOfFiles"} getSort={() => sort} setSort={(newSort) => changeSort(newSort)} getOrder={() => order} setOrder={(newOrder) => changeOrder(newOrder)} /></TableHead>
              <TableHead className="w-[9%]"><SortableTableColumnHeader label={"Size"} sortProperty={"totalFilesSize"} getSort={() => sort} setSort={(newSort) => changeSort(newSort)} getOrder={() => order} setOrder={(newOrder) => changeOrder(newOrder)} /></TableHead>
              <TableHead className="w-[10%]">Flag Type</TableHead>
              <TableHead className="w-[10%]">Blocked By</TableHead>
              <TableHead className="w-[12%]">Flag Status</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {firmDocumentsFolders.map((firmDocumentFolderEntity) => {
              const migratedPercentage =
                firmDocumentFolderEntity.statistics.totalNumberOfFiles > 0
                  ? (firmDocumentFolderEntity.statistics.filesMigrated / firmDocumentFolderEntity.statistics.totalNumberOfFiles) * 100
                  : -1;
              const isDisabled = disabledRows.has(firmDocumentFolderEntity.id);

              return (
                <TableRow key={firmDocumentFolderEntity.id}
                  className={cn("cursor-pointer", isDisabled && "opacity-50 pointer-events-none")}
                  tabIndex={0}
                  onClick={() => navigate(`/organization/${organizationId}/firm-documents-folder/${firmDocumentFolderEntity.id}`)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/organization/${organizationId}/firm-documents-folder/${firmDocumentFolderEntity.id}`); } }}>
                  <TableCell className="whitespace-normal break-all">
                    <div className="flex items-center gap-1.5">
                      <TextHighlighter text={firmDocumentFolderEntity.folderPath} searchTerm={searchForFolderPath} level="body-sm" />
                      {(firmDocumentFolderEntity.statistics.unresolvedConflictCount ?? 0) > 0 && (
                        <TooltipProvider><Tooltip><TooltipTrigger asChild>
                          <FileWarning className="size-4 shrink-0 text-destructive" aria-label="Has unresolved conflicts" />
                        </TooltipTrigger><TooltipContent>Has unresolved conflicts</TooltipContent></Tooltip></TooltipProvider>
                      )}
                      {firmDocumentFolderEntity.hasFlaggedPaths && (
                        <TooltipProvider><Tooltip><TooltipTrigger asChild>
                          <AlertTriangle className={cn("size-4 shrink-0", colorVariantClasses.warning.text)} aria-label="Has flagged paths" />
                        </TooltipTrigger><TooltipContent>Has flagged paths</TooltipContent></Tooltip></TooltipProvider>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {firmDocumentFolderEntity.isLocked ? (<Badge variant="primary"><Lock className="size-3" />Locked</Badge>) : (<></>)}
                  </TableCell>
                  <TableCell><MigrationStatusChip status={firmDocumentFolderEntity.status} /></TableCell>
                  <TableCell>
                    {firmDocumentFolderEntity.statistics.totalNumberOfFiles > 0 ? (
                      <Badge variant={migratedPercentage > 99.99 ? "success" : "primary"}>
                        {migratedPercentage % 1 > 0.99 || migratedPercentage % 1 < 0.01
                          ? `${migratedPercentage.toFixed(0)}%`
                          : `${migratedPercentage.toFixed(2)}%`}
                      </Badge>
                    ) : (
                      <Badge variant="secondary">No files</Badge>
                    )}
                  </TableCell>
                  <TableCell><Badge variant="neutral">{String(firmDocumentFolderEntity.statistics.totalNumberOfFiles ?? 0)}</Badge></TableCell>
                  <TableCell><Badge variant="neutral">{`${firmDocumentFolderEntity.statistics.filesSizeInMb.toFixed(2)} MB`}</Badge></TableCell>
                  <TableCell>
                    {firmDocumentFolderEntity.flagType ? (<FlagTypeChip flagType={firmDocumentFolderEntity.flagType} />) : (<></>)}
                  </TableCell>
                  <TableCell><span className="text-sm break-words">{getUserName(firmDocumentFolderEntity.flaggedBy)}</span></TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {firmDocumentFolderEntity.flagType == null || firmDocumentFolderEntity.flagType === FlagType.MigrationEntityBlockedByUser ? (
                      <Button
                        className="w-full max-w-[150px] justify-start text-left"
                        variant={firmDocumentFolderEntity.flagType === FlagType.MigrationEntityBlockedByUser ? "secondary" : "outline"}
                        aria-label="Block/Unblock"
                        disabled={isDisabled || userRole === UserRole.Viewer || (!isAssignedUser && userRole !== UserRole.Admin)}
                        size="sm"
                        onClick={() => {
                          firmDocumentFolderEntity.flagType === FlagType.MigrationEntityBlockedByUser
                            ? handleBlockModal(firmDocumentFolderEntity.folderPath, firmDocumentFolderEntity.id, false, true)
                            : handleBlockModal(firmDocumentFolderEntity.folderPath, firmDocumentFolderEntity.id, true, true);
                        }}>
                        {firmDocumentFolderEntity.flagType === FlagType.MigrationEntityBlockedByUser ? (<><LockOpen className="size-4 mr-1" />Unblock</>) : (<><Ban className="size-4 mr-1" />Block</>)}
                      </Button>
                    ) : (<></>)}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>{getActiveButtonForExplorer(firmDocumentFolderEntity, isDisabled)}</TableCell>
                </TableRow>
              );
            })}
              {firmDocumentsFolders.length === 0 && !isFetching && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={99}>
                    <TableEmptyState title="No firm document folders found" />
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
        <TablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          rows={rows}
          onPageChange={(newPage) => { handleChangePage(newPage); }}
          onRowsPerPageChange={(val) => { handleChangeRowsPerPage(String(val)); }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </div>

      {/* Block Modal */}
      <ConfirmMigrationEntityBlockUpdateModal
        open={isBlockModalOpen}
        isBlocked={isBlocked}
        entityKey={dirtyFolderPath}
        config={MIGRATION_ENTITY_BLOCK_UPDATE_CONFIGS.FIRM_DOCUMENTS_FOLDER}
        onClose={() => { handleBlockModal("", "", false, false); }}
        onConfirm={async () => {
          await firmDocumentsFoldersRepository.updateFirmDocumentsFolderBlock(dirtyFirmDocumentsFolderId, isBlocked);
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FIRM_DOCUMENTS_FOLDERS] }),
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FIRM_DOCUMENTS_FOLDER, dirtyFirmDocumentsFolderId] }),
          ]);
          handleBlockModal("", "", false, false);
          toast.success(isBlocked ? "Blocked" : "Unblocked");
        }}
      />
    </div>
  );
};

export default FirmDocumentsFoldersTable;
